<script setup lang="ts">
import { computed, ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { monthlySummariesApi } from '@/api/monthly-summaries';
import CareerPieChart from './career-pie-chart.vue';
import NewMembersDialog from './new-members-dialog.vue';

const props = defineProps<{
  queryParams: string;
  selectedMonthText: string;
}>();

const { data: monthlyStats } = useQuery({
  queryKey: computed(() => ['monthlyStats', props.queryParams]),
  queryFn: () => monthlySummariesApi.index(props.queryParams),
});

const hasCareerData = computed(() =>
  monthlyStats.value?.shiftsByCareer && monthlyStats.value.shiftsByCareer.length > 0,
);

const newMembersDialogOpen = ref(false);
const newMembersKind = ref<'patients' | 'caregivers'>('patients');

function openNewMembersDialog(kind: 'patients' | 'caregivers') {
  newMembersKind.value = kind;
  newMembersDialogOpen.value = true;
}
</script>

<template>
  <v-card class="pa-4">
    <v-card-title class="d-flex align-center justify-space-between pa-0">
      <v-row>
        <v-col
          cols="12"
          md="6"
        >
          <span class="text-h6 font-weight-bold">Resumen Mensual</span>
        </v-col>
      </v-row>
    </v-card-title>
    <v-row class="mt-4">
      <v-col
        cols="12"
        md="7"
      >
        <v-card
          elevation="2"
          class="mb-4"
        >
          <v-card-text>
            <p class="mb-2 font-weight-bold">
              Turnos Completados: {{ monthlyStats?.completedShiftsCount }} / {{ monthlyStats?.shiftsCount }}
            </p>
            <v-progress-linear
              :model-value="monthlyStats?.completedShiftsCount"
              :max="monthlyStats?.shiftsCount"
              color="success"
              height="20"
              rounded
            />
          </v-card-text>
        </v-card>
        <v-row>
          <v-col
            cols="12"
            md="6"
          >
            <v-card
              elevation="2"
              class="h-100"
            >
              <v-card-title class="text-subtitle-2 font-weight-bold">
                Pacientes Con Turnos
              </v-card-title>
              <v-card-text class="d-flex align-center">
                <span class="text-h4 font-weight-bold">
                  {{ monthlyStats?.patientsCount }}
                </span>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col
            cols="12"
            md="6"
          >
            <v-card
              elevation="2"
              link
              class="h-100"
              data-testid="new-patients-card"
              @click="openNewMembersDialog('patients')"
            >
              <v-card-title class="text-subtitle-2 font-weight-bold">
                Pacientes Nuevos
              </v-card-title>
              <v-card-text class="d-flex align-center justify-space-between">
                <span class="text-h4 font-weight-bold">
                  {{ monthlyStats?.newPatientsCount }}
                </span>
                <v-btn
                  size="small"
                  variant="text"
                  color="primary"
                  @click.stop="openNewMembersDialog('patients')"
                >
                  Ver listado
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row class="mt-2">
          <v-col
            cols="12"
            md="6"
          >
            <v-card
              elevation="2"
              class="h-100"
            >
              <v-card-title class="text-subtitle-2 font-weight-bold">
                Profesionales Con Turnos
              </v-card-title>
              <v-card-text class="d-flex align-center">
                <span class="text-h4 font-weight-bold">
                  {{ monthlyStats?.caregiversCount }}
                </span>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col
            cols="12"
            md="6"
          >
            <v-card
              elevation="2"
              link
              class="h-100"
              data-testid="new-caregivers-card"
              @click="openNewMembersDialog('caregivers')"
            >
              <v-card-title class="text-subtitle-2 font-weight-bold">
                Profesionales Nuevos
              </v-card-title>
              <v-card-text class="d-flex align-center justify-space-between">
                <span class="text-h4 font-weight-bold">
                  {{ monthlyStats?.newCaregiversCount }}
                </span>
                <v-btn
                  size="small"
                  variant="text"
                  color="primary"
                  @click.stop="openNewMembersDialog('caregivers')"
                >
                  Ver listado
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
      <v-col
        v-if="hasCareerData"
        cols="12"
        md="5"
      >
        <v-card elevation="2">
          <v-card-title>Turnos por tipo de especialidad</v-card-title>
          <v-card-text>
            <career-pie-chart :career-data="monthlyStats!.shiftsByCareer" />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <new-members-dialog
      v-model="newMembersDialogOpen"
      :kind="newMembersKind"
      :query-params="queryParams"
      :selected-month-text="selectedMonthText"
    />
  </v-card>
</template>
